import { useEffect, useRef, useCallback, useMemo } from 'react';
import { WorkerStateType } from '@/utils/enum';

type onReceiveDataFromWorkerCallback = (p: any) => void;

interface UseWorkerStreamResult {
  /** 主线程向worker线程发送数据的接口；返回生成的 frameId（用于诊断配对） */
  dispatchRawDataToWorker: (
    buffer: ArrayBuffer,
    sendEpoch?: number
  ) => string | undefined;
}

/**
 * 用于新建一个worker线程
 * 内部管理worker的新建、销毁、通信等逻辑细节
 *
 * @export
 * @param {UseWorkerStreamProps} props
 * @return {*}  {UseWorkerStreamResult}
 */
export function useWorkerStream(
  onReceiveDataFromWorker: onReceiveDataFromWorkerCallback
): UseWorkerStreamResult {
  // 记录唯一的worker实例
  const workerRef = useRef<Worker | null>(null);
  // 记录最新的onData回调
  const onReceiveDataFromWorkerRef = useRef<onReceiveDataFromWorkerCallback>(
    onReceiveDataFromWorker
  );

  const stateRef = useRef<WorkerStateType>(WorkerStateType.CREATING);
  const sendQueueRef = useRef<Array<any>>([]);
  const idCounterRef = useRef<number>(0);

  /**
   * 更新onData回调函数
   */
  useEffect(() => {
    onReceiveDataFromWorkerRef.current = onReceiveDataFromWorker;
  }, [onReceiveDataFromWorker]);

  useEffect(() => {
    let cancelled = false;

    stateRef.current = WorkerStateType.CREATING;

    let worker: Worker | null = null;
    try {
      worker = new Worker(
        new URL(
          /* webpackChunkName: "parse-perf-worker" */ './protoDecoder.worker-ParsePerf.ts',
          import.meta.url
        ),
        { type: 'module' }
      );
    } catch (err) {
      console.error('failed to create worker', err);
      stateRef.current = WorkerStateType.ERROR;
      return;
    }
    if (cancelled) {
      try {
        worker && worker.terminate();
      } catch (_) {}
      return;
    }

    if (!worker) {
      stateRef.current = WorkerStateType.ERROR;
      return;
    }

    workerRef.current = worker;

    workerRef.current.onmessage = (e: MessageEvent) => {
      const data = e.data;
      // 处理就绪消息
      if (data && data.type === 'READY') {
        stateRef.current = WorkerStateType.READY;
        // flush queue
        if (sendQueueRef.current.length > 0) {
          const q = sendQueueRef.current.splice(0);
          for (const item of q) {
            try {
              if (!workerRef.current) break;
              // item: { id, buffer, sendEpoch }
              if (item && item.buffer instanceof ArrayBuffer) {
                workerRef.current.postMessage(
                  {
                    id: item.id,
                    buffer: item.buffer,
                    sendEpoch: item.sendEpoch,
                  },
                  [item.buffer]
                );
              }
            } catch (err) {
              console.error('postMessage queued buffer failed:', err);
            }
          }
        }

        return;
      }

      // 正常 payload 回调
      if (onReceiveDataFromWorkerRef.current) {
        onReceiveDataFromWorkerRef.current(data);
      }
    };

    workerRef.current.onerror = (err: any) => {
      console.error('Worker error:', err);
      stateRef.current = WorkerStateType.ERROR;
    };

    return () => {
      cancelled = true;
      try {
        if (workerRef.current) workerRef.current.terminate();
      } catch (_) {}
    };
  }, []);

  const dispatchRawDataToWorker = useCallback(
    (data: ArrayBuffer, sendEpoch?: number) => {
      if (!(data instanceof ArrayBuffer)) return undefined;

      const id = `${Date.now()}_${++idCounterRef.current}`;

      // 若未就绪则入队等待（存储 id + buffer + sendEpoch）
      if (stateRef.current !== WorkerStateType.READY || !workerRef.current) {
        sendQueueRef.current.push({ id, buffer: data, sendEpoch });
        return id;
      }

      try {
        if (!workerRef.current) return undefined;
        if (data instanceof ArrayBuffer) {
          workerRef.current.postMessage({ id, buffer: data, sendEpoch }, [
            data,
          ]);
          return id;
        }
      } catch (_) {
        return undefined;
      }
      return undefined;
    },
    []
  );

  return useMemo(
    () => ({ dispatchRawDataToWorker }),
    [dispatchRawDataToWorker]
  );
}
